import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import { Button } from "@components/library";
import { useTranslation } from "@helpers/translate";
import {
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  PROFILE_VERIFICATION_PAGE,
} from "@constants";
import Input from "@components/library/UI/Input";
import {
  useDispatch,
  useSelector,
} from "react-redux";
import { useCountries } from "@components/pages/MyProfile/components/ProfileTab/hooks/useCountries";
import CountrySelect from "@components/common/CountrySelect/CountrySelect";

import ArrowRightIcon from "@assets/icons/ArrowRightIcon";
import ArrowVerifyBackIcon from "@assets/icons/ArrowVerifyBackIcon";
import FirstNameIcon from "@assets/icons/FirstNameIcon";
import LastNameIcon from "@assets/icons/LastNameIcon";
import BirthdayIcon from "@assets/icons/BirthdayIcon";

import { setData } from "@actions/verification/setData";

import "./StepOne.sass";

const TOTAL_STEPS = 3;

const STEP_BY_ROUTE = {
  "personal-information": 1,
  "id-verification": 2,
  result: 3,
};

const parseDateBirth = (value) => {
  if (
    !value ||
    typeof value !== "string"
  ) {
    return {
      day: "",
      month: "",
      year: "",
    };
  }

  const raw = value.trim();

  const isoMatch = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})$/,
  );

  if (isoMatch) {
    const [
      ,
      year,
      month,
      day,
    ] = isoMatch;

    return {
      day,
      month,
      year,
    };
  }

  const slashMatch = raw.match(
    /^(\d{2})\/(\d{2})\/(\d{4})$/,
  );

  if (slashMatch) {
    const [
      ,
      day,
      month,
      year,
    ] = slashMatch;

    return {
      day,
      month,
      year,
    };
  }

  return {
    day: "",
    month: "",
    year: "",
  };
};

const getCountryCode = (country) => {
  if (!country) return "";

  if (typeof country === "string") {
    return country;
  }

  return (
    country.code_alpha2 ||
    country.alpha2 ||
    country.iso2 ||
    country.code ||
    ""
  );
};

export const StepOne = () => {
  const { __ } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const { locale } = useSelector(
    (state) => state.localization,
  );

  const user = useSelector(
    (state) => state.user.user || {},
  );

  const verification = useSelector(
    (state) => state.verification,
  );

  const { countries } =
    useCountries(locale);

  const [errors, setErrors] =
    useState({});

  const firstName =
    user.first_name ||
    verification.firstName ||
    "";

  const lastName =
    user.last_name ||
    verification.lastName ||
    "";

  const country =
    getCountryCode(user.country) ||
    getCountryCode(
      verification.country,
    ) ||
    "";

  const dateBirth =
    user.date_birth ||
    verification.dateBirth ||
    "";

  const dobParts = useMemo(
    () => parseDateBirth(dateBirth),
    [dateBirth],
  );

  useEffect(() => {
    const nextData = {};

    if (
      firstName &&
      verification.firstName !==
        firstName
    ) {
      nextData.firstName =
        firstName;
    }

    if (
      lastName &&
      verification.lastName !==
        lastName
    ) {
      nextData.lastName = lastName;
    }

    if (
      country &&
      verification.country !==
        country
    ) {
      nextData.country = country;
    }

    if (
      dateBirth &&
      verification.dateBirth !==
        dateBirth
    ) {
      nextData.dateBirth =
        dateBirth;
    }

    if (
      user.email &&
      verification.email !==
        user.email
    ) {
      nextData.email = user.email;
    }

    if (
      user.id &&
      verification.userId !==
        user.id
    ) {
      nextData.userId = user.id;
    }

    if (
      Object.keys(nextData).length >
      0
    ) {
      dispatch(setData(nextData));
    }
  }, [
    dispatch,
    firstName,
    lastName,
    country,
    dateBirth,
    user.email,
    user.id,
    verification.firstName,
    verification.lastName,
    verification.country,
    verification.dateBirth,
    verification.email,
    verification.userId,
  ]);

  const handleNext = () => {
    const newErrors = {};

    if (!firstName.trim()) {
      newErrors.firstName = __(
        "verification.error.step1.firstName",
      );
    }

    if (!lastName.trim()) {
      newErrors.lastName = __(
        "verification.error.step1.lastName",
      );
    }

    if (!country) {
      newErrors.country = __(
        "verification.error.step1.country",
      );
    }

    if (!dateBirth) {
      newErrors.dateBirth = __(
        "verification.error.step1.dateBirth",
      );
    }

    setErrors(newErrors);

    if (
      Object.keys(newErrors).length >
      0
    ) {
      return;
    }

    navigate(
      `${PROFILE_VERIFICATION_PAGE}/id-verification`,
    );
  };

  const currentStep = (() => {
    const match =
      location.pathname.match(
        /\/profile\/verification\/([^/?#]+)/,
      );

    const route = match?.[1];

    return (
      STEP_BY_ROUTE[route] || 1
    );
  })();

  return (
    <div className="pv-start">
      <div className="pv-start__wrapper">
        <p className="pv-start__page">
          {__("verification.step")}{" "}
          {currentStep}/{TOTAL_STEPS}
        </p>

        <h2 className="pv-start__title">
          {__(
            "verification.step1.header",
          )}
        </h2>

        <div className="pv-start__content">
          <div className="pv-start-grid">
            <div className="pv-start-field">
              <Input
                className="bo-input--icon"
                data={{
                  label: __(
                    "verification.step1.firstName",
                  ),
                  type: "text",
                  placeholder: __(
                    "verification.step1.firstName",
                  ),
                  name: "firstName",
                  error: Boolean(
                    errors.firstName,
                  ),
                  settings: {
                    value: firstName,
                    readOnly: true,
                    tabIndex: -1,
                    "aria-readonly":
                      true,
                  },
                }}
              >
                <span className="bo-input__icon bo-input__icon--left">
                  <FirstNameIcon className="pv-start__input-icon" />
                </span>
              </Input>

              {errors.firstName && (
                <p className="input-message input-message--error">
                  {errors.firstName}
                </p>
              )}
            </div>

            <div className="pv-start-field">
              <Input
                className="bo-input--icon"
                data={{
                  label: __(
                    "verification.step1.lastName",
                  ),
                  type: "text",
                  placeholder: __(
                    "verification.step1.lastName",
                  ),
                  name: "lastName",
                  error: Boolean(
                    errors.lastName,
                  ),
                  settings: {
                    value: lastName,
                    readOnly: true,
                    tabIndex: -1,
                    "aria-readonly":
                      true,
                  },
                }}
              >
                <span className="bo-input__icon bo-input__icon--left">
                  <LastNameIcon className="pv-start__input-icon" />
                </span>
              </Input>

              {errors.lastName && (
                <p className="input-message input-message--error">
                  {errors.lastName}
                </p>
              )}
            </div>

            <div className="bo-input pv-start-field">
              <label className="bo-input__label">
                <p className="bo-input__label-text">
                  {__(
                    "verification.step1.country",
                  )}
                </p>

                <div
                  className="bo-input__wrap pv-start-select pv-start-select--readonly"
                  aria-disabled="true"
                  style={{
                    pointerEvents:
                      "none",
                  }}
                >
                  <CountrySelect
                    countries={
                      countries
                    }
                    value={country}
                    locale={locale}
                    error={Boolean(
                      errors.country,
                    )}
                    disabled
                    onChange={() => {}}
                    placeholder={__(
                      "verification.step1.country_select_placeholder",
                    )}
                  />
                </div>

                {errors.country && (
                  <p className="input-message input-message--error">
                    {errors.country}
                  </p>
                )}
              </label>
            </div>

            <div className="bo-input pv-start-field pv-start-dob">
              <label className="bo-input__label">
                <p className="bo-input__label-text">
                  {__(
                    "verification.step1.dob",
                  )}
                </p>

                <div className="bo-input__wrap pv-start-dob__wrap">
                  <span className="bo-input__icon bo-input__icon--left">
                    <BirthdayIcon className="pv-start__input-icon" />
                  </span>

                  <div
                    className={`pv-start-dob__inputs ${
                      errors.dateBirth
                        ? "is-error"
                        : ""
                    }`}
                  >
                    <input
                      type="text"
                      className="pv-start-dob__input"
                      placeholder="DD"
                      value={
                        dobParts.day
                      }
                      readOnly
                      tabIndex={-1}
                      aria-readonly="true"
                    />

                    <span className="pv-start-dob__separator">
                      /
                    </span>

                    <input
                      type="text"
                      className="pv-start-dob__input"
                      placeholder="MM"
                      value={
                        dobParts.month
                      }
                      readOnly
                      tabIndex={-1}
                      aria-readonly="true"
                    />

                    <span className="pv-start-dob__separator">
                      /
                    </span>

                    <input
                      type="text"
                      className="pv-start-dob__input pv-start-dob__input--year"
                      placeholder="YYYY"
                      value={
                        dobParts.year
                      }
                      readOnly
                      tabIndex={-1}
                      aria-readonly="true"
                    />
                  </div>
                </div>

                {errors.dateBirth && (
                  <p className="input-message input-message--error">
                    {errors.dateBirth}
                  </p>
                )}
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="pv-start__buttons">
        <Link
          to={
            PROFILE_VERIFICATION_PAGE
          }
          className="pv-start__back-btn"
        >
          <ArrowVerifyBackIcon />

          {__(
            "verification.buttons.back",
          )}
        </Link>

        <Button
          onClick={handleNext}
          color="orange"
          size="middle"
          className="pv-start__next-btn"
        >
          {__(
            "verification.buttons.next",
          )}

          <ArrowRightIcon />
        </Button>
      </div>
    </div>
  );
};

export default StepOne;
